import { useEffect, useState } from 'react';
import { cn } from 'lib/utils';

/**
 * Masonry layout for FeatureCards: hands each card to whichever column is currently
 * shortest, so a tall card no longer leaves dead space beside it.
 *
 * USE THIS AD HOC, NOT BY DEFAULT. It is the right answer for one situation only: a screen
 * with enough EQUAL WIDTH cards that one of them being much taller than the rest leaves a
 * visible hole. SolarChargeController is that screen - eight cards, one of them thirteen
 * rows. Everywhere else, keep the `grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-12`
 * the other feature screens use.
 *
 * Specifically, do NOT reach for it when:
 *
 *   - any card needs its own width. Masonry columns are equal by construction, so a card
 *     can never be wider than one column. AstroDim5, AstroDimSensor and SensorMode all have
 *     an xl:col-span-12 chart or schedule card, and AstroDimSensor's 2/3/4/3 widths are a
 *     deliberate composition. A grid expresses that; this cannot.
 *   - there are only two or three cards. Nothing to balance, and the grid keeps the cards
 *     in the order they are written, left to right, which reads better.
 *   - the card order is meaningful ACROSS columns. Packing is tallest-first, so a card can
 *     move to a different column than the one it was written in. Order is preserved only
 *     within a column.
 *
 * A hybrid works where a screen is mostly equal-width with one full-width card: leave the
 * full-width one outside this component, above or below it.
 *
 * Why not CSS. A grid makes every row as tall as its tallest cell, which is the hole this
 * exists to close. CSS multi-column has no rows but fills strictly in document order and
 * never backfills - once a tall card does not fit in the remainder of a column it starts the
 * next one and drags everything after it along, which reproduces the hole one column over.
 * grid-template-rows: masonry would do it natively, but no shipping browser supports it.
 */

// Tailwind's lg / xl breakpoints. The distribution happens in JS, so a lg:/xl: class cannot
// reach it and the numbers have to be repeated here.
const LG = 1024;
const XL = 1280;

const columnsForViewport = () => {
    const w = typeof window === 'undefined' ? XL : window.innerWidth;
    if (w >= XL) return 3;
    if (w >= LG) return 2;
    return 1;
};

/**
 * Distributes cards into `columns` buckets, tallest first, then restores the caller's order
 * inside each bucket. Exported separately from the component so it can be reasoned about and
 * tested without a DOM.
 *
 * @param cards    [{ weight, node }] - weight is a relative height, not pixels. A card that
 *                 is a header plus N uniform rows weighs 1 + N, which needs no measuring and
 *                 no second layout pass; give anything else (a chart, a table) an explicit
 *                 weight instead, since its height cannot be inferred from a row count.
 * @param columns  how many columns to fill.
 * @param pinFirst keep cards[0] at the top of column 1 rather than packing it. Use it when
 *                 the first card drives the others - SolarChargeController's Battery card
 *                 holds the selectors that decide which cards exist at all, and a
 *                 tallest-first pass would otherwise strand it on the far right.
 *
 * Tallest-first matters more than it sounds: packing in source order hands the tall card to
 * a column that small cards have already been dealt into, and it overhangs all of them. On
 * SolarChargeController that is columns of 10/9/18 rather than 11/14/12.
 */
export const columniseCards = (cards, columns, pinFirst = true) => {
    const indexed = cards.map((c, i) => ({ weight: c.weight, node: c.node, index: i }));
    if (columns <= 1 || indexed.length === 0) {
        return [indexed.map((c) => c.node)];
    }

    const cols = [];
    for (let i = 0; i < columns; i++) {
        cols.push({ height: 0, items: [] });
    }
    const place = (c) => {
        const shortest = cols.reduce((a, b) => (b.height < a.height ? b : a));
        shortest.items.push(c);
        shortest.height += c.weight;
    };

    let rest = indexed;
    if (pinFirst) {
        place(indexed[0]);
        rest = indexed.slice(1);
    }
    rest.slice()
        .sort((a, b) => b.weight - a.weight || a.index - b.index)
        .forEach(place);

    return cols.map((c) => c.items.sort((a, b) => a.index - b.index).map((x) => x.node));
};

/**
 * @param cards     [{ weight, node }]. Give every node a key - they are reordered.
 * @param columns   fixed column count. Omit to follow the viewport (1 / 2 / 3).
 * @param pinFirst  see columniseCards. Default true.
 */
const CardMasonry = ({ cards, columns, pinFirst = true, className }) => {
    const [auto, setAuto] = useState(columnsForViewport);

    useEffect(() => {
        if (columns) {
            return undefined;
        }
        const onResize = () => setAuto(columnsForViewport());
        // Once on mount as well: the first render used the viewport at construction time,
        // which is stale if the pane was resized before this screen was opened.
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [columns]);

    return (
        <div className={cn('flex items-start gap-4', className)}>
            {columniseCards(cards, columns || auto, pinFirst).map((column, i) => (
                <div key={i} className="flex min-w-0 flex-1 flex-col gap-4">
                    {column}
                </div>
            ))}
        </div>
    );
};

export default CardMasonry;
